import React, { useRef, useState } from "react";
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NumberFormat from "react-number-format";
import * as d3 from "d3";
import CopyTablesObjectv2 from "../../components/xlsx-export/CopyTablesObjectv2";
import { StyledTableRow } from "../../util/customFunctions";

const useStyles = makeStyles({
  root: {
    display: "flex",
    justifyContent: "center",
  },
  paper: {
    width: "100%",
    maxWidth: 600,
    padding: "2rem",
    margin: "1rem auto",
  },
  colHeader: {
    fontSize: "0.85rem",
    border: "0.5px solid lightgrey",
    padding: "5px 8px",
    color: "white",
    fontWeight: 600,
    cursor: "default",
  },
  dataCell: {
    fontSize: "0.85rem",
    border: "0.5px solid lightgrey",
    padding: "5px 8px",
    color: "inherit",
    fontWeight: "inherit",
    verticalAlign: "middle",
  },
  totalRow: {
    fontWeight: 600,
    backgroundColor: "#f2f2f2",
  },
  textField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: "28px",
      padding: 0,
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      fontSize: "0.85rem",
    },
    "& .MuiInputAdornment-root": {
      marginRight: "4px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  editableCell: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
});

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: { name: props.name, value: values.value },
        });
      }}
      thousandSeparator
      isNumericString
      allowNegative={true}
    />
  );
}

const JudgementSection = ({
  judgementValues,
  currencySymbol = "$",
  valueLabel,
  onJudgementChange,
  includeForForecastRollup,
}) => {
  const classes = useStyles();
  const tableRef = useRef();
  const [editingCategory, setEditingCategory] = useState(null);
  const [tempValue, setTempValue] = useState(""); // Store temporary value while editing

  const total = Object.values(judgementValues || {}).reduce(
    (sum, val) => sum + (parseFloat(val) || 0),
    0
  );

  const handleDoubleClick = (category) => {
    setEditingCategory(category);
    setTempValue(judgementValues[category] || ""); // Set temp value from current value
  };

  const handleBlur = () => {
    if (editingCategory) {
      onJudgementChange(editingCategory, tempValue); // Only update on blur
    }
    setEditingCategory(null);
    setTempValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBlur(); // Update on Enter
    }
  };

  const isEditing = (category) => {
    return editingCategory === category;
  };

  return (
    <Grid container className={classes.root}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <Typography align="center" variant="h2" style={{ fontWeight: 600 }}>
            Judgement
          </Typography>
        </Grid>
        <Grid item xs={2} style={{ textAlign: "right" }}>
          <CopyTablesObjectv2 tableRef={tableRef} />
        </Grid>
      </Grid>

      <TableContainer style={{ maxHeight: "60vh", border: "0.5px solid lightgrey" }}>
        <Table
          ref={tableRef}
          size="small"
          data-test="judgement-table"
          style={{ tableLayout: "fixed" }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                className={classes.colHeader}
                style={{ backgroundColor: "#4471c4", width: "50%" }}
              >
                Forecast Category
              </TableCell>
              <TableCell
                align="center"
                className={classes.colHeader}
                style={{ backgroundColor: "#5b9bd5", width: "50%" }}
              >
                {valueLabel || "ACV"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {includeForForecastRollup.map((category, index) => (
              <StyledTableRow key={index}>
                <TableCell className={classes.dataCell}>{category}</TableCell>
                <TableCell
                  align="right"
                  className={`${classes.dataCell} ${
                    !isEditing(category) ? classes.editableCell : ""
                  }`}
                  onDoubleClick={() => handleDoubleClick(category)}
                >
                  {isEditing(category) ? (
                    <TextField
                      type="text"
                      size="small"
                      variant="outlined"
                      autoFocus
                      className={classes.textField}
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)} // Only update temp value
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {currencySymbol}
                          </InputAdornment>
                        ),
                        inputComponent: NumberFormatCustom,
                      }}
                      placeholder="0"
                      data-test={`judgement-${category
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    />
                  ) : (
                    `${currencySymbol}${d3.format(",")(
                      Math.round(judgementValues[category] || 0)
                    )}`
                  )}
                </TableCell>
              </StyledTableRow>
            ))}

            {/* Total Row */}
            <StyledTableRow className={classes.totalRow}>
              <TableCell className={classes.dataCell}>
                <strong>Total</strong>
              </TableCell>
              <TableCell align="right" className={classes.dataCell}>
                <strong data-test="judgement-total">
                  {currencySymbol}
                  {d3.format(",")(Math.round(total))}
                </strong>
              </TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

// const JudgementSection = ({
//   judgementValues,
//   currencySymbol = "$",
//   valueLabel,
//   onJudgementChange,
//   includeForForecastRollup,
// }) => {
//   const classes = useStyles();
//   const tableRef = useRef();
//   const [editingCategory, setEditingCategory] = useState(null);

//   const total = Object.values(judgementValues || {}).reduce(
//     (sum, val) => sum + (parseFloat(val) || 0),
//     0
//   );

//   const handleDoubleClick = (category) => {
//     setEditingCategory(category);
//   };

//   const handleBlur = () => {
//     setEditingCategory(null);
//   };

//   const isEditing = (category) => {
//     return editingCategory === category;
//   };

//   return (
//     <Grid container className={classes.root}>
//       <Grid container spacing={2} alignItems="center">
//         <Grid item xs={10}>
//           <Typography align="center" variant="h2" style={{ fontWeight: 600 }}>
//             Judgement
//           </Typography>
//         </Grid>
//         <Grid item xs={2} style={{ textAlign: "right" }}>
//           <CopyTablesObjectv2 tableRef={tableRef} />
//         </Grid>
//       </Grid>

//       <TableContainer style={{ maxHeight: "60vh", border: "0.5px solid lightgrey" }}>
//         <Table
//           ref={tableRef}
//           size="small"
//           data-test="judgement-table"
//           style={{ tableLayout: "fixed" }}
//         >
//           <TableHead>
//             <TableRow>
//               <TableCell
//                 align="center"
//                 className={classes.colHeader}
//                 style={{ backgroundColor: "#4471c4", width: "50%" }}
//               >
//                 Forecast Category
//               </TableCell>
//               <TableCell
//                 align="center"
//                 className={classes.colHeader}
//                 style={{ backgroundColor: "#5b9bd5", width: "50%" }}
//               >
//                 {valueLabel || "ACV"}
//               </TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {includeForForecastRollup.map((category, index) => (
//               <StyledTableRow key={index}>
//                 <TableCell className={classes.dataCell}>{category}</TableCell>
//                 <TableCell
//                   align="right"
//                   className={`${classes.dataCell} ${
//                     !isEditing(category) ? classes.editableCell : ""
//                   }`}
//                   onDoubleClick={() => handleDoubleClick(category)}
//                 >
//                   {isEditing(category) ? (
//                     <TextField
//                       type="text"
//                       size="small"
//                       variant="outlined"
//                       autoFocus
//                       className={classes.textField}
//                       value={judgementValues[category] || ""}
//                       onChange={(e) =>
//                         onJudgementChange(category, e.target.value)
//                       }
//                       onBlur={handleBlur}
//                       onKeyDown={(e) => {
//                         if (e.key === "Enter") handleBlur();
//                       }}
//                       InputProps={{
//                         startAdornment: (
//                           <InputAdornment position="start">
//                             {currencySymbol}
//                           </InputAdornment>
//                         ),
//                         inputComponent: NumberFormatCustom,
//                       }}
//                       placeholder="0"
//                       data-test={`judgement-${category
//                         .toLowerCase()
//                         .replace(/\s+/g, "-")}`}
//                     />
//                   ) : (
//                     `${currencySymbol}${d3.format(",")(
//                       Math.round(judgementValues[category] || 0)
//                     )}`
//                   )}
//                 </TableCell>
//               </StyledTableRow>
//             ))}

//             {/* Total Row */}
//             <StyledTableRow className={classes.totalRow}>
//               <TableCell className={classes.dataCell}>
//                 <strong>Total</strong>
//               </TableCell>
//               <TableCell align="right" className={classes.dataCell}>
//                 <strong data-test="judgement-total">
//                   {currencySymbol}
//                   {d3.format(",")(Math.round(total))}
//                 </strong>
//               </TableCell>
//             </StyledTableRow>
//           </TableBody>
//         </Table>
//       </TableContainer>
//     </Grid>
//   );
// };

export default JudgementSection;